import { useLocalSearchParams } from 'expo-router';
import { useMemo, useState } from 'react';
import {
    ScreenContainer,
    SegmentedControl,
    Spinner,
    View,
    YStack,
} from 'smoothie';

import { isNonMobileWeb } from '../../../../../packages/smoothie/src/constants';
import { DocumentLinkList } from '../../investment/components/documents/DocumentLinkList';
import { useFormerMemberInvestmentDocuments } from '../hooks/useFormerMemberInvestmentDocuments';

enum DocumentType {
    STATEMENT = 'statement',
    TRADE_CONFIRMATION = 'trade_confirmation',
}

const DocumentTypeName = {
    [DocumentType.STATEMENT]: 'statements',
    [DocumentType.TRADE_CONFIRMATION]: 'trade confirmations',
};

export const FormerMemberInvestmentStatements = () => {
    const [documentType, setDocumentType] = useState(DocumentType.STATEMENT);
    const { apexAccountId } = useLocalSearchParams<{
        apexAccountId: string;
    }>();

    const {
        isLoading: isLoadingDocuments,
        data: accountDocuments,
        getDocumentDownloadUrl,
    } = useFormerMemberInvestmentDocuments(apexAccountId);

    const statementDocuments = useMemo(
        () => accountDocuments?.statements ?? [],
        [accountDocuments],
    );
    const confirmationDocuments = useMemo(
        () => accountDocuments?.tradeConfirmations ?? [],
        [accountDocuments],
    );
    const selectedDocuments = useMemo(() => {
        return documentType === DocumentType.STATEMENT
            ? statementDocuments
            : confirmationDocuments;
    }, [documentType, statementDocuments, confirmationDocuments]);

    return (
        <ScreenContainer title="Investment statements" hasBackButton>
            <YStack width="100%" gap={isNonMobileWeb ? 24 : 12}>
                {isLoadingDocuments ? (
                    <Spinner />
                ) : (
                    <>
                        <View width="100%">
                            <SegmentedControl
                                options={[
                                    {
                                        label: 'Statements',
                                        value: DocumentType.STATEMENT,
                                    },
                                    {
                                        label: 'Trade confirmations',
                                        value: DocumentType.TRADE_CONFIRMATION,
                                        flexBasis: isNonMobileWeb
                                            ? undefined
                                            : 40,
                                    },
                                ]}
                                selected={documentType}
                                defaultValue={DocumentType.STATEMENT}
                                onValueChange={setDocumentType}
                            />
                        </View>
                        <DocumentLinkList
                            getDocumentDownloadUrl={getDocumentDownloadUrl}
                            accountDocuments={selectedDocuments}
                            documentTypeName={
                                DocumentTypeName[documentType] ??
                                'documents of this type'
                            }
                        />
                    </>
                )}
            </YStack>
        </ScreenContainer>
    );
};
